import {
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/base/dialog";
import { Button } from "@/components/ui/base/button";
import type {
  FieldChange,
  StripePushOutcome,
} from "@/application/merchants/business_info_edit";
import { StripeResult, type AcceptedCopy } from "./StripeResult";

interface Props {
  title: string;
  changes: FieldChange<string>[];
  outcome: StripePushOutcome | "checking";
  acceptedCopy: AcceptedCopy;
  checkingDetail: string;
  auditOk: boolean;
  retryingAudit: boolean;
  onRetryAudit: () => void;
  onEditAgain: () => void;
  onDone: () => void;
}

/**
 * The step after a save. "Edit again" stays disabled until Stripe has
 * answered and the audit entry is written, so a second save never overlaps.
 */
export function ResultStep({
  title,
  changes,
  outcome,
  acceptedCopy,
  checkingDetail,
  auditOk,
  retryingAudit,
  onRetryAudit,
  onEditAgain,
  onDone,
}: Props) {
  return (
    <>
      <DialogHeader>
        <DialogTitle>{title}</DialogTitle>
        <DialogDescription>
          {changes.map((c) => c.label).join(", ")} updated.
        </DialogDescription>
      </DialogHeader>

      <div aria-live="polite">
        <StripeResult
          outcome={outcome}
          acceptedCopy={acceptedCopy}
          checkingDetail={checkingDetail}
        />
      </div>

      {!auditOk && (
        <div className="flex items-center justify-between gap-2 text-xs text-red-600">
          <span>Audit entry didn't save.</span>
          <Button
            size="sm"
            variant="outline"
            disabled={retryingAudit}
            onClick={onRetryAudit}
          >
            Try again
          </Button>
        </div>
      )}

      <DialogFooter>
        <Button
          variant="outline"
          disabled={outcome === "checking" || !auditOk}
          onClick={onEditAgain}
        >
          Edit again
        </Button>
        <Button onClick={onDone}>Done</Button>
      </DialogFooter>
    </>
  );
}
